import { useState } from 'react';
import type {
  DocumentRenderAttachmentSummary,
  DocumentRenderSection,
  ParsedTemplateEntry,
} from '@vivport/backend/types';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { assertToolSucceeded } from '@/api/templates-api';
import { useRemoveEntry } from '@/hooks/useTemplateQueries';
import { toolErrorText } from '@/utils/tool-error';
import { AlertTriangle, Plus } from 'lucide-react';
import { TemplateEntryDialog } from './TemplateEntryDialog';

interface TemplateEntriesPanelProps {
  section: DocumentRenderSection;
  instanceRootId: number;
  rootPhotos: DocumentRenderAttachmentSummary[];
}

// Entries are the render payload's parsed list, never read out of the body here; every change is
// one of templates.addEntry / updateEntry / removeEntry.
export function TemplateEntriesPanel({ section, instanceRootId, rootPhotos }: TemplateEntriesPanelProps) {
  const [dialog, setDialog] = useState<{ entry: ParsedTemplateEntry | null } | null>(null);
  const removeEntry = useRemoveEntry();
  const facts = section.templateSection;
  if (facts === null || facts.entry === null || facts.entries === null) return null;
  const def = facts.entry;
  const entries = facts.entries;
  const photos = [...section.otherAttachments, ...rootPhotos]
    .filter((item) => item.contentType === 'PHOTO')
    .filter((item, index, all) => all.findIndex((other) => other.id === item.id) === index);

  const handleRemove = async (entry: ParsedTemplateEntry) => {
    if (!window.confirm(`Remove '${entry.title}'?`)) return;
    try {
      assertToolSucceeded(
        await removeEntry.mutateAsync({
          instanceId: instanceRootId,
          sectionCode: section.code,
          entryNumber: entry.entryNumber,
        })
      );
    } catch (error) {
      toast.error(toolErrorText(error));
    }
  };

  return (
    <div data-testid="template-entries-panel" className="mt-3 space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">Entries</p>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">No entries yet.</p>
      ) : (
        <ul className="divide-y rounded-md border bg-background text-sm">
          {entries.map((entry) => (
            <li key={entry.entryNumber} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
              <span className="text-xs text-muted-foreground">#{entry.entryNumber}</span>
              <span className="font-medium">{entry.title}</span>
              {entry.category !== null ? <Badge variant="secondary">{entry.category}</Badge> : null}
              {entry.warnings.length > 0 ? (
                <Badge variant="outline" title={entry.warnings.join('\n')} className="gap-1">
                  <AlertTriangle className="h-3 w-3" aria-hidden /> Needs attention
                </Badge>
              ) : null}
              <span className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setDialog({ entry })}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void handleRemove(entry)}
                  disabled={removeEntry.isPending}
                >
                  Remove
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="outline" className="max-sm:h-11" onClick={() => setDialog({ entry: null })}>
        <Plus className="mr-1.5 h-4 w-4" /> Add {def.itemLabel}
      </Button>
      {dialog !== null ? (
        <TemplateEntryDialog
          key={dialog.entry?.entryNumber ?? 'new'}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
          section={section}
          def={def}
          instanceRootId={instanceRootId}
          photos={photos}
          entry={dialog.entry}
        />
      ) : null}
    </div>
  );
}
